/*
 * <search-modal> — the header's search panel (D-13: the native search surface).
 *
 * A native <details> holding a real GET form, so search works with JS off: the
 * panel opens, the field is typeable, submit goes to /search. This element adds
 * Dawn's modal pattern on top: focus trapped inside the open panel (trapFocus /
 * removeTrapFocus from global.js, which loads first), focus moved into the
 * input on open, Escape and the close button closing with focus restored to
 * the toggle, outside click closing WITHOUT stealing focus, and aria-expanded
 * synced on the summary. Result rows are tabindex="-1" by design — they are
 * arrow-key navigated via the nested <predictive-search> element (its own
 * assets; loaded by the header section), so the Tab ring inside the trap stays
 * input → submit → close.
 *
 * On modal close the predictive results panel is closed too — the typed term
 * is kept, so reopening restores the search where the user left it
 * (refocusing the input re-fetches).
 */
if (!customElements.get('search-modal')) {
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.search-modal__content');
      this.input = this.querySelector('input[type="search"]');
      if (!this.details) return;

      this.onToggle = () => {
        const open = this.details.open;
        if (this.summary) this.summary.setAttribute('aria-expanded', open ? 'true' : 'false');
        // QA-P1 item 2: the mobile sheet lives inside the sticky header
        // section (z-index 3), which the announcement bar section (z-index 4)
        // paints over - the top of the open sheet, field row included, sat
        // under the 48px bar. The body marker lets section-header.css lift
        // the header section above the bar only while the modal is open.
        document.body.classList.toggle('search-modal-open', open);
        if (open && this.content && this.input) {
          trapFocus(this.content, this.input);
        } else if (!open) {
          // Covers the native summary-click close path; a no-op after close().
          removeTrapFocus();
        }
      };
      this.details.addEventListener('toggle', this.onToggle);

      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        this.close();
      };
      document.addEventListener('keydown', this.onKeydown);

      this.onDocumentClick = (event) => {
        if (!this.details.open || this.contains(event.target)) return;
        this.close(false);
      };
      document.addEventListener('click', this.onDocumentClick);

      const closeButton = this.querySelector('.search-modal__close-button');
      if (closeButton) closeButton.addEventListener('click', () => this.close());

      const overlay = this.querySelector('.modal-overlay');
      if (overlay) overlay.addEventListener('click', () => this.close(false));
    }

    disconnectedCallback() {
      if (this.details) this.details.removeEventListener('toggle', this.onToggle);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.body.classList.remove('search-modal-open');
    }

    close(focusToggle = true) {
      const predictive = this.querySelector('predictive-search');
      if (predictive && typeof predictive.close === 'function') predictive.close();
      removeTrapFocus(focusToggle ? this.summary : null);
      this.details.removeAttribute('open');
      if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('search-modal', SearchModal);
}
